import React, { useEffect, useState } from 'react';
import useCredentialStep from './useCredentialStep';
import CredentialSelector from './shared/CredentialSelector';
import './OperaCloudCompare.css';

// SHARED hotel + OHIP-credential step. One hotel, one OHIP credential, with the HOTELID override a
// chain- or partner-level credential needs. Emits { hotel_id, credential } via onAdd — the shape
// dryft's create/hotels endpoints already take, and the shape the DVM Extractor wizard stores as
// credential_id + ohip_hotel_code.
//
// It was DryftAddHotel until a second app needed exactly this. Renamed rather than copied: "pick a
// hotel and the OHIP credential to read it with" is not a dryft idea, and two copies of a credential
// picker is how one of them ends up selecting by name instead of by id.
//
// `title` / `subtitle` let each caller say what the credential will be used FOR, which is the only part
// that genuinely differs between them.

const OHIP_MARKETPLACE = { id: 37, name: 'Oracle OHIP' };

const OhipHotelCredentialStep = ({
    hotels = [],
    excludeHotelIds = [],
    selectedAccount,
    selectedPartner,
    isSuperAdmin,
    isPartner,
    isChainAdmin,
    submitLabel = 'Add hotel',
    title = 'Hotel & OHIP credential',
    subtitle = 'Choose the hotel and the OHIP credential used to read it.',
    saving = false,
    error = null,
    onCancel,
    onAdd,
}) => {
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [localErrors, setLocalErrors] = useState({});

    const excluded = new Set((excludeHotelIds || []).map(String));
    const availableHotels = hotels.filter(h => !excluded.has(String(h.hotelid)));

    const cs = useCredentialStep({
        selectedMarketplace: OHIP_MARKETPLACE,
        selectedHotel,
        selectedAccount,
        selectedPartner,
        solePartnerId: selectedPartner,
        isSuperAdmin,
        isPartner,
        isChainAdmin,
    });

    // Load OHIP override defs (HOTELID etc.) once so chain/partner-level credentials prompt correctly.
    useEffect(() => { cs.loadForMarketplace(); }, []); // eslint-disable-line

    const resetOverrides = () => {
        cs.setHotelIdOverride('');
        const fields = {};
        cs.overrideDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
        cs.setCredentialOverrides(fields);
    };

    const handleSelect = (cred, values) => {
        resetOverrides();
        cs.pickWithOverrides(cred, values);
        setLocalErrors(er => ({ ...er, override: undefined }));
        setPickerOpen(false);
    };

    const handleRemove = () => {
        cs.setSelectedCredential(null);
        resetOverrides();
    };

    // credential payload matching what create.js / hotels.js remap onto addHotel.
    const instancePayload = () => {
        const p = cs.buildCredentialPayload();
        return {
            credential_id:     p.credential_id,
            account_id:        p.credential_account_id,
            hotel_id:          p.credential_hotel_id,
            partner_id:        p.credential_partner_id,
            hotel_id_override: cs.hotelIdOverride || null,
            overrides:         p.credential_overrides,
        };
    };

    const handleSubmit = () => {
        const errs = {};
        if (!selectedHotel) errs.hotel = 'Please select a hotel';
        if (!cs.selectedCredential) errs.cred = 'Please select an OHIP credential';
        const v = cs.validate();
        if (v?.hotelIdOverride) errs.override = v.hotelIdOverride;
        if (Object.keys(errs).length) { setLocalErrors(errs); return; }
        setLocalErrors({});
        onAdd({ hotel_id: selectedHotel.hotelid, credential: instancePayload() });
    };

    const credType    = cs.selectedCredential?._entityType;
    const selAboveHotel = credType === 'account' || credType === 'partner';

    return (
        <div className="ppw-card">
            <div className="ppw-card-header">
                <h2 className="ppw-card-title">{title}</h2>
                <p className="ppw-card-subtitle">{subtitle}</p>
            </div>
            <div className="ppw-card-body">
                {error && <span className="ppw-error-msg" style={{ marginBottom: '1rem', display: 'block' }}>{error}</span>}

                {/* Hotel */}
                <div className="occ-instance-block">
                    <div className="occ-instance-role">Hotel</div>
                    <select
                        className="ppw-input"
                        value={selectedHotel?.hotelid || ''}
                        onChange={e => {
                            const h = availableHotels.find(x => String(x.hotelid) === e.target.value);
                            setSelectedHotel(h || null);
                            setLocalErrors(er => ({ ...er, hotel: undefined }));
                        }}
                    >
                        <option value="">Select a hotel…</option>
                        {availableHotels.map(h => (
                            <option key={h.hotelid} value={h.hotelid}>{h.hotelname}</option>
                        ))}
                    </select>
                    {localErrors.hotel && <span className="ppw-error-msg">{localErrors.hotel}</span>}
                </div>

                {/* OHIP credential */}
                <div className="occ-instance-block">
                    <div className="occ-instance-row">
                        <div className="occ-instance-role">OHIP credential</div>
                        <CredentialSelector
                            marketplaceId={OHIP_MARKETPLACE.id}
                            hotelId={selectedHotel?.hotelid}
                            accountId={selectedAccount}
                            partnerId={selectedPartner}
                            value={cs.selectedCredential}
                            // Per-hotel fields for a chain/partner credential, asked in the picker with Save.
                            overridesInPicker
                            overrideDefs={cs.overrideDefs.length ? cs.overrideDefs : undefined}
                            overrides={cs.overrideValues}
                            overridesTitle="Per-Hotel Overrides"
                            onChange={(sel, values) => (sel ? handleSelect(sel, values) : handleRemove())}
                            canEdit={isSuperAdmin}
                            title={null}
                            subtitle="Choose an existing OHIP credential from the partner, chain or hotel — or add a new one."
                            disabled={!selectedHotel}
                            disabledReason="Choose a hotel first — a credential is shared to a hotel."
                        />
                    </div>
                    {localErrors.cred && <span className="ppw-error-msg">{localErrors.cred}</span>}
                    {selAboveHotel && cs.overrideDefs.length > 0 && localErrors.override && (
                        <span className="ppw-error-msg">{localErrors.override}</span>
                    )}
                </div>
            </div>
            <div className="ppw-footer">
                {onCancel && <button className="ppw-btn-back" onClick={onCancel} disabled={saving}>← Back</button>}
                <button className="ppw-btn-next" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
            </div>
        </div>
    );
};

export default OhipHotelCredentialStep;
